import { useState, useEffect, useRef, useMemo, type ReactNode } from "react";
import { activityCategoryStore } from "../utils/activityCategoryStore";
import ImageLibraryPicker from "../../../components/feature/ImageLibraryPicker";
import { uploadTenantImageFile } from "../../../api/tenantImageUpload";
import { useAdminData } from "../AdminDataContext";
import { reportError } from "../../../api/errors";
import { fileStore } from "../utils/dataStore";
import type { AssocFile } from "../../../api/types";
import { fetchMemberLevelBriefsForTenant, type MemberLevelBrief } from "../../../api/memberLevels";
import {
  fetchAllMemberGroupDefinitionsForTenant,
  type MemberGroupDefinition,
} from "../../../api/memberGroupDefinitions";
import {
  ACTIVITY_FORM_STEPS,
  activityFormLocationDisplay,
  emptyActivityForm,
  emptyRegistrationOptionQuestion,
  validateActivityFormStep1,
  validateActivityFormStep2,
  validateActivityFormSubmit,
  type ActivityFormData,
  type ActivityFormStep,
} from "../utils/activityFormModel";
import {
  ALL_ACTIVITY_GROUPS,
  ALL_ACTIVITY_GROUPS_LABEL,
  ALL_ACTIVITY_LEVELS,
  ALL_ACTIVITY_LEVELS_LABEL,
  toggleActivityTargetGroup,
  toggleActivityTargetLevel,
} from "../../../utils/activityTargetAudience";
import { CANCEL_DEADLINE_MODE_OPTIONS } from "../../../utils/activityCancelRegistration";
import {
  fetchPointsRulesForTenant,
  formatParticipationPointsHint,
  parseParticipationPointsInput,
  resolveParticipationPointsBounds,
  validateParticipationPoints,
  type ParticipationPointsBounds,
} from "../../../api/pointsRules";
import ActivityRegistrationOptionsEditorModal from "./ActivityRegistrationOptionsEditorModal";

export type { ActivityFormData } from "../utils/activityFormModel";
export { emptyActivityForm as emptyForm } from "../utils/activityFormModel";

const EXPENSE_TYPES = ["免费", "自费", "协会补贴", "AA制", "赞助支持", "在线支付"];

const disclaimerTypeUi: { label: string; icon: string; color: string } = {
  label: "免责声明",
  icon: "ri-shield-check-line",
  color: "bg-slate-50 text-slate-700",
};

function parseDeductionPointsInput(raw: string): number {
  const n = parseInt(String(raw || "").trim(), 10);
  return Number.isFinite(n) ? Math.max(0, n) : 0;
}

function ToggleSwitch({ checked, onChange, color = "teal" }: { checked: boolean; onChange: (v: boolean) => void; color?: "teal" | "indigo" }) {
  const onClass = color === "indigo" ? "bg-indigo-500" : "bg-teal-600";
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      className={`relative w-10 h-[22px] rounded-full transition-colors flex-shrink-0 cursor-pointer ${checked ? onClass : "bg-gray-300"}`}
    >
      <span
        className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-all ${checked ? "left-5" : "left-0.5"}`}
      />
    </button>
  );
}

function RuleGroup({ title, hint, children }: { title: string; hint?: string; children: ReactNode }) {
  return (
    <div className="overflow-hidden rounded-xl border border-gray-100 bg-white">
      <div className="border-b border-gray-50 bg-gray-50/90 px-4 py-2.5">
        <p className="text-xs font-bold text-gray-800">{title}</p>
        {hint ? <p className="text-[11px] text-gray-400 mt-0.5 leading-snug">{hint}</p> : null}
      </div>
      <div className="space-y-3 px-4 py-3">{children}</div>
    </div>
  );
}

function DateTimeField({
  label,
  day,
  time,
  timeFallback = "09:00",
  onDay,
  onTime,
}: {
  label: string;
  day: string;
  time: string;
  timeFallback?: string;
  onDay: (v: string) => void;
  onTime: (v: string) => void;
}) {
  return (
    <div className="min-w-0 flex-1 rounded-xl border border-gray-100 bg-gray-50/80 px-3 py-2.5">
      <label className="block text-xs font-medium text-gray-500 mb-1.5">{label}</label>
      <div className="flex gap-2">
        <input
          type="date"
          value={day}
          onChange={(e) => {
            onDay(e.target.value);
            if (!time.trim()) onTime(timeFallback);
          }}
          className="min-w-0 flex-1 border border-gray-200 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:border-red-400 bg-white cursor-pointer"
        />
        <input
          type="time"
          value={time.trim() || timeFallback}
          onChange={(e) => onTime(e.target.value)}
          className="w-[96px] shrink-0 border border-gray-200 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:border-red-400 bg-white cursor-pointer"
        />
      </div>
    </div>
  );
}

interface Props {
  mode: "create" | "edit";
  initialData?: ActivityFormData;
  onlinePayAmountsLocked?: boolean;
  onSubmit: (data: ActivityFormData) => void;
  onClose: () => void;
}

export default function ActivityFormModal({
  mode,
  initialData,
  onlinePayAmountsLocked = false,
  onSubmit,
  onClose,
}: Props) {
  const { tenantLegacyId, storeVersion } = useAdminData();
  const [form, setForm] = useState<ActivityFormData>(() => initialData ?? emptyActivityForm());
  const [step, setStep] = useState<ActivityFormStep>(1);
  const [stepMax, setStepMax] = useState<ActivityFormStep>(1);
  const [participationBounds, setParticipationBounds] = useState<ParticipationPointsBounds>(() =>
    resolveParticipationPointsBounds(null, "activity"),
  );
  const [audienceLevels, setAudienceLevels] = useState<MemberLevelBrief[]>([]);
  const [audienceGroups, setAudienceGroups] = useState<MemberGroupDefinition[]>([]);
  const [peerAssociations, setPeerAssociations] = useState<Array<{ id: string; name: string; shortName?: string }>>([]);

  const [showMapPicker, setShowMapPicker] = useState(false);
  const [showCoverPicker, setShowCoverPicker] = useState(false);
  const [showLibraryPicker, setShowLibraryPicker] = useState(false);
  const [regOptionsOpen, setRegOptionsOpen] = useState(false);
  const coverFileRef = useRef<HTMLInputElement>(null);
  const [fileRev, setFileRev] = useState(0);

  useEffect(() => fileStore.subscribe(() => setFileRev((n) => n + 1)), []);

  const [categoryRev, setCategoryRev] = useState(0);
  useEffect(() => activityCategoryStore.subscribe(() => setCategoryRev((n) => n + 1)), []);
  const activityCategories = useMemo(() => activityCategoryStore.getEnabled(), [categoryRev]);

  const disclaimerPublishedDocs = useMemo(
    () =>
      fileStore
        .getAll()
        .filter((f: AssocFile) => f.type === "disclaimer" && f.status === "published"),
    [storeVersion, fileRev],
  );

  useEffect(() => {
    if (initialData) setForm(initialData);
  }, [initialData]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const rules = await fetchPointsRulesForTenant(tenantLegacyId);
        if (cancelled) return;
        const bounds = resolveParticipationPointsBounds(rules, "activity");
        setParticipationBounds(bounds);
        if (mode === "create" && !initialData) {
          const defaultPts = bounds.pointsMode === "fixed" && bounds.min > 0 ? bounds.min : bounds.min > 0 ? bounds.min : 30;
          setForm(emptyActivityForm(defaultPts));
        }
      } catch {
        if (!cancelled) setParticipationBounds(resolveParticipationPointsBounds(null, "activity"));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [tenantLegacyId, mode, initialData]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const [levels, groups] = await Promise.all([
          fetchMemberLevelBriefsForTenant(tenantLegacyId),
          fetchAllMemberGroupDefinitionsForTenant(tenantLegacyId),
        ]);
        if (cancelled) return;
        setAudienceLevels(levels.filter((l) => l.isEnabled !== false));
        setAudienceGroups(groups.filter((g) => g.enabled !== false));
      } catch {
        if (!cancelled) {
          setAudienceLevels([]);
          setAudienceGroups([]);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [tenantLegacyId]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const { apiGet } = await import("../../../api/http");
        const rows = await apiGet<Array<{ id: string; name: string; shortName?: string }>>(
          `/api/tenants/${encodeURIComponent(tenantLegacyId)}/linked-associations`,
        );
        if (!cancelled) setPeerAssociations(Array.isArray(rows) ? rows : []);
      } catch {
        if (!cancelled) setPeerAssociations([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [tenantLegacyId]);

  useEffect(() => {
    const onMessage = (ev: MessageEvent) => {
      const d = ev.data as Record<string, unknown>;
      if (!d || d.module !== "locationPicker") return;
      const latlng = d.latlng as { lat?: number; lng?: number } | undefined;
      const lat = String(latlng?.lat ?? "").trim();
      const lng = String(latlng?.lng ?? "").trim();
      if (!lat || !lng) return;
      setForm((p) => ({
        ...p,
        checkInFenceLat: lat,
        checkInFenceLng: lng,
        locationMapPicked: true,
        location:
          String(d.poiaddress || d.poiname || p.location || "").trim() || p.location,
      }));
      setShowMapPicker(false);
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, []);

  const buildTencentLocPickerUrl = () => {
    const key = String((import.meta as { env?: Record<string, string> }).env?.VITE_TENCENT_MAP_KEY || "").trim();
    const referer = String((import.meta as { env?: Record<string, string> }).env?.VITE_TENCENT_MAP_REFERER || "").trim();
    if (!key || !referer) return { ok: false as const, url: "" };
    const defaultCoord = "28.6829,115.8582";
    const q = new URLSearchParams({
      search: "1",
      type: "1",
      policy: "1",
      zoom: "15",
      coord: defaultCoord,
      coordtype: "5",
      key,
      referer,
    });
    return { ok: true as const, url: `https://apis.map.qq.com/tools/locpicker?${q.toString()}` };
  };

  const set = <K extends keyof ActivityFormData>(k: K, v: ActivityFormData[K]) =>
    setForm((p) => ({ ...p, [k]: v }));

  const goStep = (next: ActivityFormStep) => {
    setStep(next);
    setStepMax((m) => (next > m ? next : m));
  };

  const tryNextStep = () => {
    if (step === 1) {
      const err = validateActivityFormStep1(form);
      if (err) {
        reportError(new Error(err), "无法继续");
        return;
      }
      goStep(2);
      return;
    }
    if (step === 2) {
      const err = validateActivityFormStep2(form);
      if (err) {
        reportError(new Error(err), "无法继续");
        return;
      }
      goStep(3);
    }
  };

  const audienceLevelPickOptions = useMemo(() => {
    const fromLevels = audienceLevels.map((l) => l.id.trim()).filter(Boolean);
    const legacy = (form.targetAudience.levels || [])
      .map((x) => String(x || "").trim())
      .filter((x) => x && x !== ALL_ACTIVITY_LEVELS && !fromLevels.includes(x));
    return [ALL_ACTIVITY_LEVELS, ...fromLevels, ...legacy];
  }, [audienceLevels, form.targetAudience.levels]);

  const audienceGroupPickOptions = useMemo(() => {
    const fromGroups = audienceGroups.map((g) => g.id.trim()).filter(Boolean);
    const legacy = (form.targetAudience.groups || [])
      .map((x) => String(x || "").trim())
      .filter((x) => x && x !== ALL_ACTIVITY_GROUPS && !fromGroups.includes(x));
    return [ALL_ACTIVITY_GROUPS, ...fromGroups, ...legacy];
  }, [audienceGroups, form.targetAudience.groups]);

  const selectedDisclaimerIds = useMemo(() => {
    const allowed = new Set(disclaimerPublishedDocs.map((d) => d.id));
    return form.requiredDocIds.filter((id) => allowed.has(id));
  }, [form.requiredDocIds, disclaimerPublishedDocs]);

  const participationHint = useMemo(
    () => formatParticipationPointsHint(participationBounds),
    [participationBounds],
  );

  const participationPointsError = useMemo(
    () => validateParticipationPoints(form.participationPoints, participationBounds),
    [form.participationPoints, participationBounds],
  );

  const canSubmit = useMemo(() => {
    const allowed = disclaimerPublishedDocs.map((d) => d.id);
    return validateActivityFormSubmit(form, allowed, participationBounds) === null;
  }, [form, disclaimerPublishedDocs, participationBounds]);

  const toggleDocId = (id: string) => {
    setForm((p) => {
      const has = p.requiredDocIds.includes(id);
      return {
        ...p,
        requiredDocIds: has ? p.requiredDocIds.filter((x) => x !== id) : [...p.requiredDocIds, id],
      };
    });
  };

  const handleFormSubmit = () => {
    const allowed = disclaimerPublishedDocs.map((d) => d.id);
    const err = validateActivityFormSubmit(form, allowed, participationBounds);
    if (err) {
      reportError(new Error(err), "无法保存");
      return;
    }
    const selectedDocIds = form.requiredDocIds.filter((id) => allowed.includes(id));
    onSubmit({
      ...form,
      requiredDocIds: form.requireDocSign ? selectedDocIds : [],
    });
  };

  const formTitle =
    mode === "edit"
      ? "编辑活动"
      : "创建活动";

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-[720px] max-w-[95vw] max-h-[90vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 pt-6 pb-3 border-b border-gray-100 flex-shrink-0">
          <div>
            <h3 className="font-bold text-gray-800">{formTitle}</h3>
            <p className="text-xs text-gray-400 mt-0.5">
              第 {step} 步 / 共 3 步 · * 为必填
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer rounded-lg hover:bg-gray-100"
          >
            <i className="ri-close-line text-lg" />
          </button>
        </div>

        {/* Step indicator */}
        <div className="px-6 pb-4 flex items-center gap-1 flex-shrink-0">
          {ACTIVITY_FORM_STEPS.map(({ step: s, label }, idx) => {
            const reached = s <= stepMax;
            const active = s === step;
            const done = s < step;
            return (
              <div key={s} className="flex-1 min-w-0 flex items-center">
                <button
                  type="button"
                  disabled={!reached || s === step}
                  onClick={() => {
                    if (!reached || s === step) return;
                    if (s === 2) {
                      const err = validateActivityFormStep1(form);
                      if (err) {
                        reportError(new Error(err), "无法跳转");
                        return;
                      }
                    }
                    if (s === 3) {
                      const err = validateActivityFormStep2(form);
                      if (err) {
                        reportError(new Error(err), "无法跳转");
                        return;
                      }
                    }
                    goStep(s);
                  }}
                  className={`flex-1 min-w-0 flex flex-col items-center cursor-pointer disabled:cursor-default ${reached ? "" : "opacity-50"}`}
                >
                  <div
                    className={`w-7 h-7 rounded-full flex items-center justify-center ${
                      active ? "bg-red-600 text-white" : done ? "bg-red-50 text-red-600" : "bg-gray-100 text-gray-500"
                    }`}
                  >
                    {done ? <i className="ri-check-line text-sm" /> : <span className="text-xs font-bold">{s}</span>}
                  </div>
                  <span
                    className={`mt-1 text-[10px] font-semibold text-center truncate w-full ${
                      active || done ? "text-red-600" : "text-gray-400"
                    }`}
                  >
                    {label}
                  </span>
                </button>
                {idx < ACTIVITY_FORM_STEPS.length - 1 ? (
                  <div className={`h-0.5 w-2 shrink-0 mx-0.5 ${s < step ? "bg-red-200" : "bg-gray-200"}`} />
                ) : null}
              </div>
            );
          })}
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {step === 1 ? (
            <div className="space-y-5">
              {/* Cover */}
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-2 flex items-center gap-1.5">
                  <i className="ri-image-2-line text-red-500" />
                  活动封面图
                  <span className="text-gray-400 font-normal">（将显示在活动卡片顶部）</span>
                </label>
                {form.coverUrl ? (
                  <div className="relative rounded-xl overflow-hidden mb-2" style={{ height: 100 }}>
                    <img src={form.coverUrl} alt="封面预览" className="w-full h-full object-cover object-top" />
                    <div className="absolute inset-0 bg-black/30 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity">
                      <button
                        type="button"
                        onClick={() => setShowCoverPicker(true)}
                        className="bg-white text-gray-800 text-xs font-medium px-3 py-1.5 rounded-lg cursor-pointer mr-2"
                      >
                        更换封面
                      </button>
                      <button
                        type="button"
                        onClick={() => set("coverUrl", "")}
                        className="bg-red-500 text-white text-xs font-medium px-3 py-1.5 rounded-lg cursor-pointer"
                      >
                        移除封面
                      </button>
                    </div>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => setShowCoverPicker(true)}
                    className="w-full h-20 border-2 border-dashed border-gray-200 rounded-xl flex items-center justify-center gap-3 text-gray-400 hover:border-red-300 hover:text-red-400 cursor-pointer transition-colors"
                  >
                    <i className="ri-image-add-line text-2xl" />
                    <div className="text-left">
                      <p className="text-sm font-medium">选择封面图</p>
                      <p className="text-xs text-gray-300 mt-0.5">本地上传、资源库或输入链接</p>
                    </div>
                  </button>
                )}
                {showCoverPicker ? (
                  <div className="border border-gray-200 rounded-xl overflow-hidden mt-2 bg-white">
                    <div className="px-4 py-2.5 border-b border-gray-100 flex items-center justify-between">
                      <p className="text-xs font-semibold text-gray-700">活动封面</p>
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => coverFileRef.current?.click()}
                          className="flex items-center gap-1 text-xs text-green-600 hover:text-green-700 cursor-pointer bg-green-50 px-2 py-1 rounded-lg"
                        >
                          <i className="ri-upload-line text-xs" /> 本地上传
                        </button>
                        <input
                          ref={coverFileRef}
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (!file) return;
                            void (async () => {
                              try {
                                const url = await uploadTenantImageFile(file, tenantLegacyId);
                                set("coverUrl", url);
                                setShowCoverPicker(false);
                              } catch (err) {
                                reportError(err, "封面上传失败");
                              }
                              if (coverFileRef.current) coverFileRef.current.value = "";
                            })();
                          }}
                        />
                        <button
                          type="button"
                          onClick={() => {
                            setShowCoverPicker(false);
                            setShowLibraryPicker(true);
                          }}
                          className="flex items-center gap-1 text-xs text-amber-600 hover:text-amber-700 cursor-pointer bg-amber-50 px-2 py-1 rounded-lg"
                        >
                          <i className="ri-image-2-line text-xs" /> 资源库
                        </button>
                        <button
                          type="button"
                          onClick={() => setShowCoverPicker(false)}
                          className="w-5 h-5 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer"
                        >
                          <i className="ri-close-line text-sm" />
                        </button>
                      </div>
                    </div>
                    <div className="px-3 pb-3 pt-2">
                      <p className="text-xs text-gray-400 mb-1.5">或输入自定义图片链接：</p>
                      <input
                        type="url"
                        placeholder="https://..."
                        className="w-full border border-gray-200 rounded-lg px-3 py-1.5 text-xs focus:outline-none focus:border-red-400"
                        onBlur={(e) => {
                          if (e.target.value.startsWith("http")) {
                            set("coverUrl", e.target.value);
                            setShowCoverPicker(false);
                          }
                        }}
                      />
                    </div>
                  </div>
                ) : null}
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1.5">活动名称 *</label>
                <input
                  value={form.title}
                  onChange={(e) => set("title", e.target.value)}
                  placeholder="请输入活动名称"
                  className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-red-400 bg-white"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-600 mb-2">活动分类 *</label>
                <div className="flex flex-wrap gap-2">
                  {activityCategories.map((cat) => (
                    <button
                      key={cat.id}
                      type="button"
                      onClick={() => set("activityType", cat.name)}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl border text-xs cursor-pointer transition-all ${
                        form.activityType === cat.name
                          ? "bg-teal-600 text-white border-teal-600"
                          : "bg-white text-gray-600 border-gray-200 hover:border-teal-300"
                      }`}
                    >
                      <i className={`${cat.icon} text-sm`} />
                      {cat.name}
                    </button>
                  ))}
                </div>
                {activityCategories.length === 0 ? (
                  <p className="mt-1.5 text-xs text-gray-400">暂无启用的活动分类，请先在活动分类设置中添加</p>
                ) : !form.activityType.trim() ? (
                  <p className="mt-1.5 text-xs text-gray-400">请选择活动分类</p>
                ) : null}
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1.5">活动标签</label>
                <input
                  value={form.tags}
                  onChange={(e) => set("tags", e.target.value)}
                  placeholder="如：观赛,线下,主场（用逗号分隔）"
                  className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-red-400 bg-white"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1.5">活动简介</label>
                <textarea
                  value={form.description}
                  onChange={(e) => set("description", e.target.value)}
                  placeholder="请简要描述活动内容..."
                  rows={3}
                  maxLength={500}
                  className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-red-400 bg-white resize-none"
                />
              </div>
            </div>
          ) : null}

          {step === 2 ? (
            <div className="space-y-5">
              <div className="rounded-xl border border-gray-100 bg-white p-4">
                <p className="text-xs font-semibold text-gray-700 mb-3">活动时间 *</p>
                <div className="space-y-3">
                  <div className="flex gap-3">
                    <DateTimeField
                      label="报名开始"
                      day={form.regStartDay}
                      time={form.regStartTime}
                      timeFallback="09:00"
                      onDay={(v) => set("regStartDay", v)}
                      onTime={(v) => set("regStartTime", v)}
                    />
                    <DateTimeField
                      label="报名截止"
                      day={form.regEndDay}
                      time={form.regEndTime}
                      timeFallback="18:00"
                      onDay={(v) => set("regEndDay", v)}
                      onTime={(v) => set("regEndTime", v)}
                    />
                  </div>
                  <div className="flex gap-3">
                    <DateTimeField
                      label="活动开始"
                      day={form.dateDay}
                      time={form.dateTime}
                      timeFallback="09:00"
                      onDay={(v) => set("dateDay", v)}
                      onTime={(v) => set("dateTime", v)}
                    />
                    <DateTimeField
                      label="活动结束"
                      day={form.endDateDay}
                      time={form.endDateTime}
                      timeFallback="18:00"
                      onDay={(v) => set("endDateDay", v)}
                      onTime={(v) => set("endDateTime", v)}
                    />
                  </div>
                </div>
              </div>

              <div className="rounded-xl border border-gray-100 bg-white p-4">
                <p className="text-xs font-semibold text-gray-700 mb-2">活动地点 *</p>
                <div className="flex items-center gap-2">
                  <input
                    value={activityFormLocationDisplay(form.location)}
                    disabled={!form.locationMapPicked}
                    onChange={(e) => {
                      if (!form.locationMapPicked) return;
                      set("location", e.target.value);
                    }}
                    placeholder={form.locationMapPicked ? "可微调地点名称" : "请先点击地图选择"}
                    className={`min-w-0 flex-1 border rounded-xl px-3 py-2.5 text-sm focus:outline-none ${
                      form.locationMapPicked
                        ? "border-gray-200 bg-white text-gray-900 focus:border-red-400"
                        : "border-gray-100 bg-gray-50 text-gray-400"
                    }`}
                  />
                  <button
                    type="button"
                    onClick={() => setShowMapPicker(true)}
                    className="shrink-0 rounded-xl border border-gray-200 bg-gray-100 px-3 py-2.5 text-xs font-semibold text-gray-700 hover:border-red-300 hover:text-red-600 cursor-pointer"
                  >
                    从地图选点
                  </button>
                  <div className="w-24 shrink-0">
                    <input
                      type="number"
                      min={1}
                      max={99999}
                      value={form.capacity > 0 ? form.capacity : ""}
                      placeholder="必填"
                      onChange={(e) => {
                        const raw = e.target.value.trim();
                        if (!raw) {
                          set("capacity", 0);
                          return;
                        }
                        const n = parseInt(raw, 10);
                        if (Number.isFinite(n)) set("capacity", Math.max(1, Math.min(99999, n)));
                      }}
                      className="w-full border border-gray-200 rounded-xl px-2 py-2.5 text-sm text-center focus:outline-none focus:border-red-400 bg-white"
                    />
                    <p className="mt-0.5 text-[10px] text-gray-400 text-center">人数上限 *</p>
                  </div>
                </div>
                {form.checkInFenceLat.trim() && form.checkInFenceLng.trim() ? (
                  <p className="mt-1.5 text-[11px] text-gray-400">
                    坐标 {form.checkInFenceLat.trim()}, {form.checkInFenceLng.trim()}
                  </p>
                ) : null}
                <div className="mt-3 flex items-center gap-2">
                  <input
                    type="number"
                    min={0}
                    value={form.checkInFenceRadiusMeters || ""}
                    placeholder="签到围栏半径（米，0 表示不限制）"
                    onChange={(e) =>
                      set("checkInFenceRadiusMeters", Math.max(0, parseInt(e.target.value, 10) || 0))
                    }
                    className="min-w-0 flex-1 border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-red-400 bg-white"
                  />
                  <button
                    type="button"
                    onClick={() =>
                      setForm((p) => ({
                        ...p,
                        checkInFenceLat: "",
                        checkInFenceLng: "",
                        checkInFenceRadiusMeters: 0,
                      }))
                    }
                    className="shrink-0 rounded-xl bg-gray-100 px-3 py-2 text-xs font-semibold text-gray-700 hover:bg-gray-200 cursor-pointer"
                  >
                    清除坐标
                  </button>
                </div>
              </div>

              <div className="rounded-xl border border-gray-100 bg-white p-4">
                <p className="text-xs font-semibold text-gray-700 mb-3">报名展示</p>
                <div className="space-y-2">
                  <div className="flex items-center justify-between rounded-xl bg-gray-50 px-3 py-2.5">
                    <div className="min-w-0 pr-3">
                      <p className="text-xs font-semibold text-gray-700">向会员展示报名人数</p>
                      <p className="text-[11px] text-gray-500 mt-0.5">开启后会员可在报名页看到当前报名人数</p>
                    </div>
                    <ToggleSwitch
                      checked={form.showEnrollmentCountToMembers}
                      onChange={(v) => set("showEnrollmentCountToMembers", v)}
                    />
                  </div>
                  <div className="flex items-center justify-between rounded-xl bg-gray-50 px-3 py-2.5">
                    <div className="min-w-0 pr-3">
                      <p className="text-xs font-semibold text-gray-700">报名页展示分享按钮</p>
                      <p className="text-[11px] text-gray-500 mt-0.5">开启后会员可在报名页一键分享活动</p>
                    </div>
                    <ToggleSwitch
                      checked={form.showShareOnRegistrationPage}
                      onChange={(v) => set("showShareOnRegistrationPage", v)}
                    />
                  </div>
                </div>
              </div>
            </div>
          ) : null}

          {step === 3 ? (
            <div className="space-y-4">
              <RuleGroup title="签到与签退">
                <div className="flex items-center justify-between gap-3 rounded-xl bg-gray-50/80 px-3 py-2">
                  <span className="text-xs text-gray-600">提前签到（分钟）</span>
                  <input
                    type="number"
                    min={0}
                    max={7 * 24 * 60}
                    value={form.checkInEarlyMinutes}
                    onChange={(e) =>
                      set("checkInEarlyMinutes", Math.max(0, Math.min(7 * 24 * 60, parseInt(e.target.value, 10) || 0)))
                    }
                    className="w-20 border border-gray-200 rounded-xl px-2 py-1.5 text-sm text-center focus:outline-none focus:border-teal-400 bg-white"
                  />
                </div>
                <div className="rounded-xl bg-gray-50/80 px-3 py-2.5">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-medium text-gray-700">限制开始后签到</span>
                    <ToggleSwitch
                      checked={form.checkInLateLimitEnabled}
                      onChange={(v) => set("checkInLateLimitEnabled", v)}
                    />
                  </div>
                  {form.checkInLateLimitEnabled ? (
                    <div className="mt-2 flex items-center justify-between gap-2">
                      <span className="text-[11px] text-gray-500">活动开始后多少分钟内仍可签到</span>
                      <input
                        type="number"
                        min={0}
                        max={7 * 24 * 60}
                        value={form.checkInLateMinutes}
                        onChange={(e) =>
                          set("checkInLateMinutes", Math.max(0, Math.min(7 * 24 * 60, parseInt(e.target.value, 10) || 0)))
                        }
                        className="w-20 border border-gray-200 rounded-xl px-2 py-1.5 text-sm text-center focus:outline-none focus:border-teal-400 bg-white"
                      />
                    </div>
                  ) : (
                    <p className="mt-1 text-[11px] text-gray-400">不限制：至活动结束时刻前均可签到</p>
                  )}
                </div>
                <div className="rounded-xl bg-gray-50/80 px-3 py-2.5">
                  <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-xs font-medium text-gray-700">要求签退</p>
                      <p className="text-[11px] text-gray-400 mt-0.5">开启后积分在签退后发放，签到不记分</p>
                    </div>
                    <ToggleSwitch
                      checked={form.requireCheckOut}
                      onChange={(v) =>
                        setForm((p) => ({
                          ...p,
                          requireCheckOut: v,
                          ...(!v ? { checkOutAllowEarly: false, checkOutEarlyMinutes: 0 } : {}),
                        }))
                      }
                    />
                  </div>
                  {form.requireCheckOut ? (
                    <div className="mt-2 space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-gray-600">允许提前签退</span>
                        <ToggleSwitch
                          checked={form.checkOutAllowEarly}
                          onChange={(v) =>
                            setForm((p) => ({
                              ...p,
                              checkOutAllowEarly: v,
                              ...(!v ? { checkOutEarlyMinutes: 0 } : {}),
                            }))
                          }
                        />
                      </div>
                      {form.checkOutAllowEarly ? (
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-[11px] text-gray-500">相对活动结束时间，可提前多少分钟签退</span>
                          <input
                            type="number"
                            min={0}
                            max={7 * 24 * 60}
                            value={form.checkOutEarlyMinutes}
                            onChange={(e) =>
                              set("checkOutEarlyMinutes", Math.max(0, Math.min(7 * 24 * 60, parseInt(e.target.value, 10) || 0)))
                            }
                            className="w-20 border border-gray-200 rounded-xl px-2 py-1.5 text-sm text-center focus:outline-none focus:border-teal-400 bg-white"
                          />
                        </div>
                      ) : null}
                    </div>
                  ) : null}
                </div>
              </RuleGroup>

              <RuleGroup title="经费与取消">
                <div className="flex flex-wrap gap-2">
                  {EXPENSE_TYPES.map((t) => {
                    const feeLocked = onlinePayAmountsLocked;
                    return (
                    <button
                      key={t}
                      type="button"
                      disabled={feeLocked}
                      onClick={() =>
                        setForm((p) => ({
                          ...p,
                          expenseType: t,
                          ...(t !== "在线支付"
                            ? { onlinePayAmountYuanText: "", onlinePayDepositYuanText: "" }
                            : {}),
                        }))
                      }
                      className={`px-3 py-1.5 rounded-xl border text-xs transition-all ${
                        feeLocked ? "opacity-60 cursor-not-allowed" : "cursor-pointer"
                      } ${
                        form.expenseType === t
                          ? "bg-teal-600 text-white border-teal-600"
                          : "bg-white text-gray-600 border-gray-200 hover:border-teal-300"
                      }`}
                    >
                      {t}
                    </button>
                    );
                  })}
                </div>
                {form.expenseType === "在线支付" ? (
                  <div className="flex flex-col gap-2">
                    {(() => {
                      const feeLocked = onlinePayAmountsLocked;
                      return (
                        <>
                          {feeLocked ? (
                            <p className="text-xs text-amber-600">已有报名/登记，基础费用与押金不可修改</p>
                          ) : null}
                          <div className="flex items-center gap-2">
                            <span className="w-20 shrink-0 text-xs text-gray-500">基础费用（元）</span>
                            <input
                              type="text"
                              inputMode="decimal"
                              disabled={feeLocked}
                              value={form.onlinePayAmountYuanText}
                              placeholder="0.01"
                              onChange={(e) => {
                                if (feeLocked) return;
                                const raw = e.target.value.trim();
                                if (!raw) {
                                  set("onlinePayAmountYuanText", "");
                                  return;
                                }
                                const hit = raw.match(/^(\d+)(\.(\d{0,2})?)?$/);
                                if (!hit) return;
                                set("onlinePayAmountYuanText", `${hit[1]}${hit[2] ?? ""}`);
                              }}
                              className={`min-w-0 flex-1 max-w-[200px] border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-teal-400 ${feeLocked ? "bg-gray-100 text-gray-500" : "bg-white"}`}
                            />
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="w-20 shrink-0 text-xs text-gray-500">报名押金（元）</span>
                            <input
                              type="text"
                              inputMode="decimal"
                              disabled={feeLocked}
                              value={form.onlinePayDepositYuanText}
                              placeholder="选填，结束后自动退"
                              onChange={(e) => {
                                if (feeLocked) return;
                                const raw = e.target.value.trim();
                                if (!raw) {
                                  set("onlinePayDepositYuanText", "");
                                  return;
                                }
                                const hit = raw.match(/^(\d+)(\.(\d{0,2})?)?$/);
                                if (!hit) return;
                                set("onlinePayDepositYuanText", `${hit[1]}${hit[2] ?? ""}`);
                              }}
                              className={`min-w-0 flex-1 max-w-[200px] border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-teal-400 ${feeLocked ? "bg-gray-100 text-gray-500" : "bg-white"}`}
                            />
                          </div>
                        </>
                      );
                    })()}
                  </div>
                ) : null}
                <div className="flex items-center justify-between rounded-xl bg-gray-50/80 px-3 py-2.5">
                  <div className="min-w-0 pr-3">
                    <p className="text-xs font-medium text-gray-700">允许取消报名并自动退款</p>
                    <p className="text-[11px] text-gray-400 mt-0.5">会员可在截止前取消报名并自动退款</p>
                  </div>
                  <ToggleSwitch
                    checked={form.allowCancelWithRefund}
                    onChange={(v) => set("allowCancelWithRefund", v)}
                  />
                </div>
                {form.allowCancelWithRefund ? (
                  <div>
                    <div className="flex flex-wrap gap-2">
                      {CANCEL_DEADLINE_MODE_OPTIONS.map((opt) => (
                        <button
                          key={opt.value}
                          type="button"
                          onClick={() =>
                            setForm((p) => ({
                              ...p,
                              cancelDeadlineMode: opt.value,
                              ...(opt.value !== "custom" ? { cancelDeadlineDay: "", cancelDeadlineTime: "" } : {}),
                            }))
                          }
                          className={`px-3 py-1.5 rounded-xl border text-xs cursor-pointer ${
                            form.cancelDeadlineMode === opt.value
                              ? "bg-teal-600 text-white border-teal-600"
                              : "bg-white text-gray-600 border-gray-200 hover:border-teal-300"
                          }`}
                        >
                          {opt.label}
                        </button>
                      ))}
                    </div>
                    {form.cancelDeadlineMode === "custom" ? (
                      <div className="mt-2 max-w-xs">
                        <DateTimeField
                          label="自定义取消截止时间"
                          day={form.cancelDeadlineDay}
                          time={form.cancelDeadlineTime}
                          timeFallback="18:00"
                          onDay={(v) => set("cancelDeadlineDay", v)}
                          onTime={(v) => set("cancelDeadlineTime", v)}
                        />
                      </div>
                    ) : (
                      <p className="mt-1.5 text-[11px] text-gray-400">
                        {form.cancelDeadlineMode === "reg_end"
                          ? "取消截止时间与报名截止一致"
                          : "相对活动开始时间"}
                      </p>
                    )}
                  </div>
                ) : null}
              </RuleGroup>

              <div className="rounded-xl border border-gray-100 bg-white p-4 space-y-3">
                <div className="flex items-center gap-2 rounded-xl bg-gray-50/80 px-3 py-2.5">
                  <span className="min-w-0 flex-1 text-xs font-medium text-gray-700">允许会员代亲朋好友登记</span>
                  <ToggleSwitch
                    checked={form.allowMemberFriendGuestRegistration}
                    onChange={(v) => set("allowMemberFriendGuestRegistration", v)}
                  />
                  <input
                    type="number"
                    min={1}
                    max={99999}
                    disabled={!form.allowMemberFriendGuestRegistration}
                    value={form.memberFriendGuestRegistrationQuota > 0 ? form.memberFriendGuestRegistrationQuota : ""}
                    placeholder="2"
                    onChange={(e) => {
                      if (!form.allowMemberFriendGuestRegistration) return;
                      const raw = e.target.value.trim();
                      if (!raw) {
                        set("memberFriendGuestRegistrationQuota", 0);
                        return;
                      }
                      const n = parseInt(raw, 10);
                      if (Number.isFinite(n)) set("memberFriendGuestRegistrationQuota", Math.max(0, Math.min(99999, n)));
                    }}
                    className={`w-16 shrink-0 border rounded-xl px-2 py-1.5 text-sm text-center focus:outline-none ${
                      form.allowMemberFriendGuestRegistration
                        ? "border-gray-200 bg-white focus:border-teal-400"
                        : "border-gray-100 bg-gray-100 text-gray-400"
                    }`}
                  />
                  <span className="shrink-0 text-[10px] text-gray-400">人/会员</span>
                </div>

                <div className="flex items-center gap-2 rounded-xl bg-gray-50/80 px-3 py-2.5">
                  <span className="min-w-0 flex-1 text-xs font-medium text-gray-700">启用报名活动选项</span>
                  <ToggleSwitch
                    checked={form.registrationOptions.enabled}
                    onChange={(v) =>
                      setForm((p) => ({
                        ...p,
                        registrationOptions: v
                          ? {
                              enabled: true,
                              items: p.registrationOptions.items.length
                                ? p.registrationOptions.items
                                : [emptyRegistrationOptionQuestion()],
                            }
                          : { enabled: false, items: [] },
                      }))
                    }
                  />
                  <button
                    type="button"
                    disabled={!form.registrationOptions.enabled}
                    onClick={() => setRegOptionsOpen(true)}
                    className={`shrink-0 max-w-[44%] rounded-lg border px-2.5 py-1.5 text-[11px] font-semibold cursor-pointer ${
                      form.registrationOptions.enabled
                        ? "border-teal-100 bg-teal-50 text-teal-800 hover:bg-teal-100"
                        : "border-gray-200 bg-gray-100 text-gray-400 cursor-not-allowed"
                    }`}
                  >
                    {form.registrationOptions.items.some((q) => q.title.trim())
                      ? `已配置 ${form.registrationOptions.items.length} 题`
                      : "配置选项"}
                  </button>
                </div>

                <div>
                  <p className="text-xs font-medium text-gray-700 mb-2">活动目标对象</p>
                  <p className="text-[11px] text-gray-400 mb-1.5">会员等级（可多选，默认所有等级）</p>
                  <div className="flex flex-wrap gap-2 mb-3">
                    {audienceLevelPickOptions.map((opt) => {
                      const selected = form.targetAudience.levels.includes(opt);
                      const label =
                        opt === ALL_ACTIVITY_LEVELS
                          ? ALL_ACTIVITY_LEVELS_LABEL
                          : audienceLevels.find((l) => l.id === opt)?.name || opt;
                      return (
                        <button
                          key={`lv-${opt}`}
                          type="button"
                          onClick={() =>
                            setForm((p) => ({
                              ...p,
                              targetAudience: toggleActivityTargetLevel(p.targetAudience, opt),
                            }))
                          }
                          className={`px-3 py-1.5 rounded-xl border text-xs cursor-pointer ${
                            selected ? "bg-amber-500 text-white border-amber-500" : "bg-white text-gray-600 border-gray-200 hover:border-amber-300"
                          }`}
                        >
                          {label}
                        </button>
                      );
                    })}
                  </div>
                  <p className="text-[11px] text-gray-400 mb-1.5">会员分组（可多选，默认所有分组）</p>
                  <div className="flex flex-wrap gap-2">
                    {audienceGroupPickOptions.map((opt) => {
                      const selected = form.targetAudience.groups.includes(opt);
                      const label =
                        opt === ALL_ACTIVITY_GROUPS
                          ? ALL_ACTIVITY_GROUPS_LABEL
                          : audienceGroups.find((g) => g.id === opt)?.name || opt;
                      return (
                        <button
                          key={`gp-${opt}`}
                          type="button"
                          onClick={() =>
                            setForm((p) => ({
                              ...p,
                              targetAudience: toggleActivityTargetGroup(p.targetAudience, opt),
                            }))
                          }
                          className={`px-3 py-1.5 rounded-xl border text-xs cursor-pointer ${
                            selected ? "bg-sky-500 text-white border-sky-500" : "bg-white text-gray-600 border-gray-200 hover:border-sky-300"
                          }`}
                        >
                          {label}
                        </button>
                      );
                    })}
                  </div>
                  {peerAssociations.length > 0 ? (
                    <div className="mt-4">
                      <p className="text-[11px] text-gray-400 mb-1.5">关联协会（可多选，名额与本协会互不挤占）</p>
                      <div className="space-y-2">
                        {peerAssociations.map((peer) => {
                          const row = form.linkedAssociations.find((x) => x.tenantId === peer.id);
                          const selected = Boolean(row);
                          return (
                            <div
                              key={peer.id}
                              className={`flex flex-wrap items-center gap-2 rounded-xl border px-3 py-2 ${
                                selected ? "border-amber-300 bg-amber-50/60" : "border-gray-200 bg-white"
                              }`}
                            >
                              <button
                                type="button"
                                onClick={() =>
                                  setForm((p) => {
                                    const exists = p.linkedAssociations.some((x) => x.tenantId === peer.id);
                                    if (exists) {
                                      return {
                                        ...p,
                                        linkedAssociations: p.linkedAssociations.filter((x) => x.tenantId !== peer.id),
                                      };
                                    }
                                    return {
                                      ...p,
                                      linkedAssociations: [
                                        ...p.linkedAssociations,
                                        { tenantId: peer.id, tenantName: peer.name, quota: 0 },
                                      ],
                                    };
                                  })
                                }
                                className={`px-2.5 py-1 rounded-lg border text-xs cursor-pointer ${
                                  selected
                                    ? "bg-amber-500 text-white border-amber-500"
                                    : "bg-white text-gray-600 border-gray-200"
                                }`}
                              >
                                {peer.name}
                              </button>
                              {selected ? (
                                <label className="flex items-center gap-1.5 text-xs text-gray-600">
                                  名额
                                  <input
                                    type="number"
                                    min={0}
                                    value={row?.quota ?? 0}
                                    onChange={(e) => {
                                      const q = Math.max(0, Math.floor(Number(e.target.value) || 0));
                                      setForm((p) => ({
                                        ...p,
                                        linkedAssociations: p.linkedAssociations.map((x) =>
                                          x.tenantId === peer.id ? { ...x, quota: q } : x,
                                        ),
                                      }));
                                    }}
                                    className="w-20 border border-gray-200 rounded-lg px-2 py-1 text-xs"
                                  />
                                </label>
                              ) : null}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ) : null}
                </div>

                <div className="flex items-center gap-2 rounded-xl bg-gray-50/80 px-3 py-2.5">
                  <span className="shrink-0 text-xs font-medium text-gray-700">年龄限制</span>
                  <span className="shrink-0 text-[11px] text-gray-400">0=不限</span>
                  <input
                    type="number"
                    min={0}
                    max={120}
                    value={form.ageMin || ""}
                    placeholder="最小"
                    onChange={(e) => set("ageMin", Math.max(0, parseInt(e.target.value, 10) || 0))}
                    className="min-w-0 flex-1 border border-gray-200 rounded-xl px-2 py-1.5 text-sm text-center focus:outline-none focus:border-red-400 bg-white"
                  />
                  <span className="text-xs text-gray-400">至</span>
                  <input
                    type="number"
                    min={0}
                    max={120}
                    value={form.ageMax || ""}
                    placeholder="最大"
                    onChange={(e) => set("ageMax", Math.max(0, parseInt(e.target.value, 10) || 0))}
                    className="min-w-0 flex-1 border border-gray-200 rounded-xl px-2 py-1.5 text-sm text-center focus:outline-none focus:border-red-400 bg-white"
                  />
                </div>

                <div className="rounded-xl bg-gray-50/80 px-3 py-2.5">
                  <div className="flex items-center gap-2">
                    <span className="shrink-0 text-xs font-medium text-gray-700">参与活动积分奖励</span>
                    <input
                      type="number"
                      min={0}
                      value={form.participationPoints || ""}
                      placeholder="0"
                      onChange={(e) => set("participationPoints", parseParticipationPointsInput(e.target.value))}
                      className={`min-w-0 flex-1 border rounded-xl px-2 py-1.5 text-sm text-center focus:outline-none bg-white ${
                        participationPointsError ? "border-red-300 focus:border-red-400" : "border-gray-200 focus:border-amber-400"
                      }`}
                    />
                  </div>
                  {participationPointsError ? (
                    <p className="mt-1 text-[11px] text-red-500">{participationPointsError}</p>
                  ) : form.participationPoints > 0 ? (
                    <p className="mt-1 text-[11px] text-amber-700">参与即得 {form.participationPoints} 积分</p>
                  ) : (
                    <p className="mt-1 text-[11px] text-gray-400 leading-snug">{participationHint}</p>
                  )}
                </div>

                <div className="rounded-xl bg-gray-50/80 px-3 py-2.5">
                  <div className="flex items-center justify-between gap-2 mb-2">
                    <div>
                      <p className="text-xs font-medium text-gray-700">积分扣除</p>
                      <p className="text-[11px] text-gray-400">未报名 / 未签到时扣除</p>
                    </div>
                    <ToggleSwitch
                      checked={form.enableParticipationPointDeduction}
                      onChange={(v) => set("enableParticipationPointDeduction", v)}
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="w-12 shrink-0 text-[11px] text-gray-500">未报名</span>
                    <input
                      type="number"
                      min={0}
                      disabled={!form.enableParticipationPointDeduction}
                      value={form.notRegisteredDeductionPoints || ""}
                      placeholder="0"
                      onChange={(e) => {
                        if (!form.enableParticipationPointDeduction) return;
                        set("notRegisteredDeductionPoints", parseDeductionPointsInput(e.target.value));
                      }}
                      className={`min-w-0 flex-1 border rounded-xl px-2 py-1.5 text-sm text-center focus:outline-none ${
                        form.enableParticipationPointDeduction
                          ? "border-gray-200 bg-white focus:border-teal-400"
                          : "border-gray-100 bg-gray-100 text-gray-400"
                      }`}
                    />
                    <span className="w-12 shrink-0 text-[11px] text-gray-500">未签到</span>
                    <input
                      type="number"
                      min={0}
                      disabled={!form.enableParticipationPointDeduction}
                      value={form.notCheckedInDeductionPoints || ""}
                      placeholder="0"
                      onChange={(e) => {
                        if (!form.enableParticipationPointDeduction) return;
                        set("notCheckedInDeductionPoints", parseDeductionPointsInput(e.target.value));
                      }}
                      className={`min-w-0 flex-1 border rounded-xl px-2 py-1.5 text-sm text-center focus:outline-none ${
                        form.enableParticipationPointDeduction
                          ? "border-gray-200 bg-white focus:border-teal-400"
                          : "border-gray-100 bg-gray-100 text-gray-400"
                      }`}
                    />
                  </div>
                </div>

                <div className="rounded-2xl border border-gray-200 overflow-hidden">
                  <div
                    className="flex items-center justify-between px-4 py-3.5 cursor-pointer hover:bg-gray-50 transition-colors"
                    onClick={() => {
                      set("requireDocSign", !form.requireDocSign);
                      if (form.requireDocSign) set("requiredDocIds", []);
                    }}
                  >
                    <div className="flex items-center gap-3">
                      <div
                        className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                          form.requireDocSign ? "bg-indigo-50 text-indigo-600" : "bg-gray-100 text-gray-400"
                        }`}
                      >
                        <i className="ri-file-text-line text-sm" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-800">报名者需要签署文件</p>
                        <p className="text-xs text-gray-400 mt-0.5">
                          {form.requireDocSign
                            ? `已选 ${selectedDisclaimerIds.length} 份免责声明`
                            : "开启后从文件管理中选择已发布的免责声明"}
                        </p>
                      </div>
                    </div>
                    <ToggleSwitch
                      checked={form.requireDocSign}
                      onChange={(v) => {
                        set("requireDocSign", v);
                        if (!v) set("requiredDocIds", []);
                      }}
                      color="indigo"
                    />
                  </div>
                  {form.requireDocSign ? (
                    <div className="border-t border-gray-100 bg-gray-50/60 px-4 py-4">
                      {disclaimerPublishedDocs.length === 0 ? (
                        <div className="text-center py-4 text-xs text-gray-400">
                          暂无已发布的免责声明，请先在「文件管理」中新建并发布
                        </div>
                      ) : (
                        <div className="space-y-2">
                          {disclaimerPublishedDocs.map((doc) => {
                            const cfg = disclaimerTypeUi;
                            const selected = form.requiredDocIds.includes(doc.id);
                            return (
                              <button
                                key={doc.id}
                                type="button"
                                onClick={() => toggleDocId(doc.id)}
                                className={`w-full text-left flex items-center gap-3 px-4 py-3 rounded-xl border-2 cursor-pointer transition-all ${
                                  selected
                                    ? "border-indigo-400 bg-indigo-50"
                                    : "border-gray-200 bg-white hover:border-indigo-200"
                                }`}
                              >
                                <div className={`w-8 h-8 flex items-center justify-center rounded-lg flex-shrink-0 ${cfg.color}`}>
                                  <i className={`${cfg.icon} text-sm`} />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2">
                                    <p className="text-sm font-medium text-gray-800 truncate">{doc.name}</p>
                                    <span className={`text-xs px-1.5 py-0.5 rounded-full flex-shrink-0 ${cfg.color}`}>
                                      {cfg.label}
                                    </span>
                                  </div>
                                  {doc.requireSignature ? (
                                    <p className="text-xs text-amber-600 mt-0.5">此文件已设置需电子签名</p>
                                  ) : null}
                                </div>
                                <div
                                  className={`w-5 h-5 rounded-md border-2 flex items-center justify-center flex-shrink-0 ${
                                    selected ? "bg-indigo-500 border-indigo-500" : "border-gray-300"
                                  }`}
                                >
                                  {selected ? <i className="ri-check-line text-white text-xs" /> : null}
                                </div>
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  ) : null}
                </div>

                <div className="flex items-center justify-between rounded-xl bg-gray-50/80 px-3 py-2.5">
                  <div className="min-w-0 pr-3">
                    <p className="text-xs font-medium text-gray-700">启用抽奖</p>
                    <p className="text-[11px] text-gray-400 mt-0.5">开启后可在活动详情配置抽奖奖项</p>
                  </div>
                  <ToggleSwitch checked={form.lotteryEnabled} onChange={(v) => set("lotteryEnabled", v)} />
                </div>
              </div>
            </div>
          ) : null}
        </div>

        {/* Footer navigation */}
        <div className="flex items-center gap-3 px-6 py-4 border-t border-gray-100 flex-shrink-0 bg-white">
          {step === 1 ? (
            <>
              <button
                type="button"
                onClick={onClose}
                className="flex-1 py-2.5 text-sm text-gray-600 border border-gray-200 rounded-xl hover:bg-gray-50 cursor-pointer"
              >
                取消
              </button>
              <button
                type="button"
                onClick={tryNextStep}
                className="flex-[2] py-2.5 text-sm font-semibold text-white bg-red-600 rounded-xl hover:bg-red-700 cursor-pointer"
              >
                下一步
              </button>
            </>
          ) : step === 2 ? (
            <>
              <button
                type="button"
                onClick={() => goStep(1)}
                className="flex-1 py-2.5 text-sm text-gray-600 border border-gray-200 rounded-xl hover:bg-gray-50 cursor-pointer"
              >
                上一步
              </button>
              <button
                type="button"
                onClick={tryNextStep}
                className="flex-[2] py-2.5 text-sm font-semibold text-white bg-red-600 rounded-xl hover:bg-red-700 cursor-pointer"
              >
                下一步
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                onClick={() => goStep(2)}
                className="flex-1 py-2.5 text-sm text-gray-600 border border-gray-200 rounded-xl hover:bg-gray-50 cursor-pointer"
              >
                上一步
              </button>
              <button
                type="button"
                onClick={handleFormSubmit}
                disabled={!canSubmit}
                className="flex-[2] py-2.5 text-sm font-semibold text-white bg-red-600 rounded-xl hover:bg-red-700 cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {mode === "create" ? "保存草稿" : "保存修改"}
              </button>
            </>
          )}
        </div>
      </div>

      {showMapPicker ? (
        <div className="fixed inset-0 z-[60] bg-black/50 flex items-center justify-center p-4">
          <div className="w-[900px] max-w-[95vw] rounded-2xl bg-white overflow-hidden">
            <div className="flex items-center justify-between border-b border-gray-100 px-4 py-3">
              <div>
                <p className="text-sm font-semibold text-gray-800">地图选点</p>
                <p className="text-[11px] text-gray-400 mt-0.5">
                  需要配置腾讯地图 locpicker（VITE_TENCENT_MAP_KEY / VITE_TENCENT_MAP_REFERER）
                </p>
              </div>
              <button
                type="button"
                onClick={() => setShowMapPicker(false)}
                className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer rounded-lg hover:bg-gray-100"
              >
                <i className="ri-close-line text-lg" />
              </button>
            </div>
            {(() => {
              const built = buildTencentLocPickerUrl();
              if (!built.ok) {
                return (
                  <div className="px-4 py-6">
                    <p className="text-sm font-semibold text-gray-800">缺少腾讯地图配置</p>
                    <p className="mt-2 text-xs text-gray-600">
                      请在构建/运行环境配置 VITE_TENCENT_MAP_KEY 与 VITE_TENCENT_MAP_REFERER。
                    </p>
                  </div>
                );
              }
              return (
                <iframe
                  title="locpicker"
                  className="w-full"
                  style={{ height: 520 }}
                  src={built.url}
                  allow="geolocation"
                />
              );
            })()}
          </div>
        </div>
      ) : null}

      {showLibraryPicker ? (
        <ImageLibraryPicker
          tenantLegacyId={tenantLegacyId}
          defaultCategory="activity"
          title="从资源库选择活动封面"
          onSelect={(img) => {
            set("coverUrl", img.url);
            setShowLibraryPicker(false);
          }}
          onClose={() => setShowLibraryPicker(false)}
        />
      ) : null}

      <ActivityRegistrationOptionsEditorModal
        open={regOptionsOpen}
        initialValue={form.registrationOptions}
        onClose={() => setRegOptionsOpen(false)}
        onSave={(value) => {
          set("registrationOptions", value);
          setRegOptionsOpen(false);
        }}
      />
    </div>
  );
}
